"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCatalogSettings = void 0;
const react_1 = require("react");
const react_redux_1 = require("react-redux");
const catalog_1 = require("../client/catalog");
const get_contracts_hook_1 = require("../client/contract/get-contracts.hook");
const product_tree_1 = require("../client/product-tree");
const projects_1 = require("../client/projects");
const view_query_params_1 = require("../common/view-query-params");
const hooks_1 = require("../../hooks");
const localization_1 = require("../../localization");
const store_1 = require("../../store");
const useCatalogSettings = () => {
    const dispatch = (0, react_redux_1.useDispatch)();
    const { addError } = (0, hooks_1.useNotification)();
    const { formatMessage: t } = (0, localization_1.useLocalization)();
    const { viewQueryState } = (0, view_query_params_1.useViewQueryStateQueryParam)();
    const [projectIdFromUrl, setProjectIdFromUrl] = (0, react_1.useState)(viewQueryState === null || viewQueryState === void 0 ? void 0 : viewQueryState[view_query_params_1.ViewQueryStateField.ProjectId]);
    const [catalogIdFromUrl, setCatalogIdFromUrl] = (0, react_1.useState)(viewQueryState === null || viewQueryState === void 0 ? void 0 : viewQueryState[view_query_params_1.ViewQueryStateField.CatalogId]);
    const projectId = (0, store_1.useSelectedProjectId)();
    const projectSlug = (0, store_1.useSelectedProjectSlug)();
    const catalogId = (0, store_1.useSelectedCatalogId)();
    const effectiveProjectId = projectIdFromUrl || projectId;
    const effectiveCatalogId = catalogIdFromUrl || catalogId;
    const { projects } = (0, projects_1.useProjects)();
    const { catalogs } = (0, catalog_1.useCatalogs)({ projectId: effectiveProjectId });
    const handleSelectStrategyTree = (0, react_1.useCallback)((productTree) => dispatch((0, store_1.enrichAISetProductTree)(productTree)), [dispatch]);
    const handleSelectCatalog = (0, react_1.useCallback)((catalog) => dispatch((0, store_1.enrichAISetSelectedCatalog)(catalog)), [dispatch]);
    const handleSelectProject = (0, react_1.useCallback)((project) => dispatch((0, store_1.enrichAISetSelectedProject)(project)), [dispatch]);
    const handleSetProjectTags = (0, react_1.useCallback)((id, tags) => dispatch((0, store_1.enrichAISetProjectTags)(id, tags)), [dispatch]);
    const selectedProject = (0, react_1.useMemo)(() => {
        if (!(projects === null || projects === void 0 ? void 0 : projects.length)) {
            return null;
        }
        if (effectiveProjectId) {
            return projects.find(({ id }) => id === effectiveProjectId) || projects[0];
        }
        // select the first project in the list for the first time
        return projects[0];
    }, [effectiveProjectId, projects]);
    (0, react_1.useEffect)(() => {
        if (!selectedProject)
            return;
        if (selectedProject.id !== projectId || selectedProject.slug !== projectSlug) {
            handleSelectProject({
                id: selectedProject.id,
                slug: selectedProject.slug,
            });
        }
        if (projectIdFromUrl && projectId === selectedProject.id) {
            setProjectIdFromUrl(null);
        }
    }, [projectId, projectIdFromUrl, projectSlug, selectedProject, handleSelectProject]);
    const selectedProjectId = selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.id;
    const { contracts } = (0, get_contracts_hook_1.useContracts)(selectedProjectId);
    (0, react_1.useEffect)(() => {
        var _a;
        if (selectedProjectId && (contracts === null || contracts === void 0 ? void 0 : contracts.length)) {
            const { allowedOptions = [] } = (_a = contracts[0]) !== null && _a !== void 0 ? _a : {};
            const tags = Object.keys(allowedOptions).map((item) => ({ key: item, value: String(allowedOptions[item]) }));
            handleSetProjectTags(selectedProjectId, tags);
        }
    }, [selectedProjectId, contracts, handleSetProjectTags]);
    const selectedCatalog = (0, react_1.useMemo)(() => {
        // catalog id can be set to null when creating a new catalog
        if (!(catalogs === null || catalogs === void 0 ? void 0 : catalogs.length)) {
            return null;
        }
        if (effectiveCatalogId) {
            return catalogs.find(({ id }) => id === effectiveCatalogId) || catalogs[0];
        }
        // select the first project in the list for the first time
        return catalogs[0];
    }, [catalogs, effectiveCatalogId]);
    (0, react_1.useEffect)(() => {
        if (!selectedCatalog)
            return;
        if (selectedCatalog.id !== catalogId) {
            const { id, title } = selectedCatalog;
            handleSelectCatalog({ id, title });
        }
        if (catalogIdFromUrl && catalogId === selectedCatalog.id) {
            setCatalogIdFromUrl(null);
        }
    }, [catalogId, catalogIdFromUrl, selectedCatalog, handleSelectCatalog]);
    const { data: selectedCatalogProductsTree, error: selectedCatalogProductsTreeError, } = (0, product_tree_1.useCatalogProductTree)(selectedCatalog === null || selectedCatalog === void 0 ? void 0 : selectedCatalog.id, { skip: !(selectedCatalog === null || selectedCatalog === void 0 ? void 0 : selectedCatalog.id) });
    (0, react_1.useEffect)(() => {
        var _a, _b;
        if (selectedCatalogProductsTree) {
            const { id, title, root, myPermissions, tags, } = selectedCatalogProductsTree !== null && selectedCatalogProductsTree !== void 0 ? selectedCatalogProductsTree : {};
            if (!id || selectedCatalogProductsTreeError) {
                dispatch(addError(t({ key: 'GENERAL_ERROR_MESSAGE' })));
            }
            handleSelectStrategyTree({
                id,
                title,
                permissions: myPermissions,
                tags: (_b = (_a = tags === null || tags === void 0 ? void 0 : tags.edges) === null || _a === void 0 ? void 0 : _a.map((item) => item.node)) !== null && _b !== void 0 ? _b : [],
                rootNodeId: root === null || root === void 0 ? void 0 : root.id,
            });
        }
    }, [addError, dispatch, handleSelectStrategyTree, selectedCatalogProductsTree, selectedCatalogProductsTreeError, t]);
    return {
        isReady: (!projectIdFromUrl || projectId === (selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.id)) && (!catalogIdFromUrl || catalogId === (selectedCatalog === null || selectedCatalog === void 0 ? void 0 : selectedCatalog.id)),
        selectedCatalogProductsTree: selectedCatalogProductsTree === null || selectedCatalogProductsTree === void 0 ? void 0 : selectedCatalogProductsTree.id,
        selectedCatalogId: selectedCatalog === null || selectedCatalog === void 0 ? void 0 : selectedCatalog.id,
        selectedProjectId: selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.id,
    };
};
exports.useCatalogSettings = useCatalogSettings;
